import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Smartphone, Download, Globe, ExternalLink, LogOut, Link as LinkIcon } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('nutrinori_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('nutrinori_token');
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('nutrinori_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#3D6B4A' }}></div>
        </div>
      </AdminLayout>
    );
  }

  if (!stats) return null;

  const osData = [
    { name: 'iOS', value: stats.ios_clicks },
    { name: 'Android', value: stats.android_clicks },
    { name: 'Desktop', value: stats.desktop_clicks }
  ];

  return (
    <AdminLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0A120D' }} data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="mt-2" style={{ color: '#6B756D' }}>Vue d'ensemble des statistiques</p>
        </div>

        {/* Main Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="card" data-testid="total-clicks-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#6B756D' }}>Total Clics</span>
              <TrendingUp className="w-5 h-5" style={{ color: '#3D6B4A' }} />
            </div>
            <div className="text-3xl font-bold" style={{ color: '#0A120D' }} data-testid="total-clicks-value">
              {stats.total_clicks.toLocaleString()}
            </div>
          </div>

          <div className="card" data-testid="ios-clicks-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#6B756D' }}>iOS</span>
              <Smartphone className="w-5 h-5" style={{ color: '#3D6B4A' }} />
            </div>
            <div className="text-3xl font-bold" style={{ color: '#0A120D' }} data-testid="ios-clicks-value">
              {stats.ios_clicks.toLocaleString()}
            </div>
          </div>

          <div className="card" data-testid="android-clicks-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#6B756D' }}>Android</span>
              <Download className="w-5 h-5" style={{ color: '#3D6B4A' }} />
            </div>
            <div className="text-3xl font-bold" style={{ color: '#0A120D' }} data-testid="android-clicks-value">
              {stats.android_clicks.toLocaleString()}
            </div>
          </div>

          <div className="card" data-testid="desktop-clicks-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: '#6B756D' }}>Desktop</span>
              <Globe className="w-5 h-5" style={{ color: '#3D6B4A' }} />
            </div>
            <div className="text-3xl font-bold" style={{ color: '#0A120D' }} data-testid="desktop-clicks-value">
              {stats.desktop_clicks.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Charts and Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* OS Distribution Chart */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A120D' }} data-testid="os-chart-title">
              Distribution par OS
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={osData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(61, 107, 74, 0.1)" />
                <XAxis dataKey="name" stroke="#6B756D" />
                <YAxis stroke="#6B756D" />
                <Tooltip />
                <Bar dataKey="value" fill="#3D6B4A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top Countries */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A120D' }} data-testid="top-countries-title">
              Top Pays
            </h2>
            <div className="space-y-3">
              {stats.top_countries.length > 0 ? (
                stats.top_countries.map((country, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b" style={{ borderColor: 'rgba(61, 107, 74, 0.1)' }}>
                    <span style={{ color: '#3A453F' }}>{country.country}</span>
                    <span className="font-semibold" style={{ color: '#3D6B4A' }}>{country.count}</span>
                  </div>
                ))
              ) : (
                <p style={{ color: '#6B756D' }}>Aucune donnée disponible</p>
              )}
            </div>
          </div>
        </div>

        {/* Top Referrers */}
        <div className="card mb-8">
          <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A120D' }} data-testid="top-referrers-title">
            Top Sources de Trafic
          </h2>
          <div className="space-y-3">
            {stats.top_referrers.length > 0 ? (
              stats.top_referrers.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b" style={{ borderColor: 'rgba(61, 107, 74, 0.1)' }}>
                  <span style={{ color: '#3A453F' }}>{ref.referrer}</span>
                  <span className="font-semibold" style={{ color: '#3D6B4A' }}>{ref.count}</span>
                </div>
              ))
            ) : (
              <p style={{ color: '#6B756D' }}>Aucune donnée disponible</p>
            )}
          </div>
        </div>

        {/* Recent Clicks */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A120D' }} data-testid="recent-clicks-title">
            Clics Récents
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(61, 107, 74, 0.15)' }}>
                  <th className="text-left py-3 px-2 text-sm font-medium" style={{ color: '#6B756D' }}>Date</th>
                  <th className="text-left py-3 px-2 text-sm font-medium" style={{ color: '#6B756D' }}>OS</th>
                  <th className="text-left py-3 px-2 text-sm font-medium" style={{ color: '#6B756D' }}>Pays</th>
                  <th className="text-left py-3 px-2 text-sm font-medium" style={{ color: '#6B756D' }}>Source</th>
                  <th className="text-left py-3 px-2 text-sm font-medium" style={{ color: '#6B756D' }}>Lien</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_clicks.map((click, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid rgba(61, 107, 74, 0.05)' }}>
                    <td className="py-3 px-2 text-sm" style={{ color: '#3A453F' }}>
                      {new Date(click.timestamp).toLocaleString('fr-FR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </td>
                    <td className="py-3 px-2">
                      <span className="px-2 py-1 rounded text-xs font-medium" style={{ 
                        backgroundColor: '#E8F0EA', 
                        color: '#3D6B4A' 
                      }}>
                        {click.os}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-sm" style={{ color: '#3A453F' }}>{click.country}</td>
                    <td className="py-3 px-2 text-sm" style={{ color: '#3A453F' }}>{click.referrer}</td>
                    <td className="py-3 px-2 text-sm" style={{ color: '#3A453F' }}>{click.slug}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
